import { eventBus, CollectionUpdatedEvent } from '@theme/events';
import { filterStore } from '@theme/collection--filter--state--store';

/**
 * Collection menu soft navigation.
 *
 * Tapping a category in the collection menu used to be a full navigation:
 * tear the page down, re-parse ~1 MB of HTML, re-run every component and every
 * third-party script (~2 s of main-thread work on a phone) before the user
 * could interact again — with no visual reaction for the first second.
 *
 * Now a tap on a `.collection-menu__link`:
 *   1. reacts in the same frame (active tab moves, grid dims with its spinner);
 *   2. fetches the target PLP (warmed on touchstart, 100–300 ms before the tap);
 *   3. swaps `#MainContent` with the fetched one, updates title / canonical /
 *      body template data, pushes history and scrolls to top.
 *
 * Only `#MainContent` changes: header, footer, cart, third-party scripts and
 * every module already loaded stay as they are. Custom elements inside the new
 * content upgrade on insertion; module scripts the new template needs and the
 * current page did not load are appended once.
 *
 * Back/forward re-run the same swap for the restored URL. The filter store
 * keeps handling its own `?filter.*` history entries; it ignores pathname
 * changes (see `handlePopState` there) and is told the new rendered URL here.
 *
 * Anything unexpected (non-OK response, no `#MainContent` in the response,
 * network error) falls back to a normal navigation to the same URL.
 */

const TOUCH_PRIMARY = window.matchMedia?.('(hover: none)').matches ?? false;
const STATE_KEY = 'collectionNav';
const WARM_TTL_MS = 30_000;
const LINK_SELECTOR = '.collection-menu__link';

/** @type {Map<string, { promise: Promise<string>, at: number }>} */
const warm = new Map();
/** @type {AbortController|null} */
let pending = null;
let renderedPath = location.pathname + location.search;

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * @param {HTMLAnchorElement|null} a
 * @returns {boolean}
 */
function isSoftTarget(a) {
	if (!a || !a.href) return false;
	if (a.origin !== location.origin) return false;
	if (a.target && a.target !== '_self') return false;
	if (a.hasAttribute('download') || a.hasAttribute('data-no-soft-nav')) return false;
	if (!/(^|\/)collections\//.test(a.pathname)) return false;
	return true;
}

/**
 * @param {string} url
 * @returns {Promise<string>}
 */
function fetchHtml(url, signal) {
	return fetch(url, { signal, headers: { Accept: 'text/html' } }).then((response) => {
		if (!response.ok) throw new Error(`Soft navigation: ${response.status} for ${url}`);
		return response.text();
	});
}

/** Warm the HTTP response for a link the finger just landed on. */
function warmUp(href) {
	const entry = warm.get(href);
	if (entry && Date.now() - entry.at < WARM_TTL_MS) return;
	const promise = fetchHtml(href).catch(() => {
		warm.delete(href);
		return '';
	});
	warm.set(href, { promise, at: Date.now() });
}

/** @type {HTMLElement|null} */
let spinner = null;

/**
 * Feedback for the tap: active tab moves now, the grid dims through its own
 * overlay, and a spinner that lives outside #MainContent (so the DOM swap
 * doesn't take it away) turns on its own compositor layer while the main
 * thread is busy parsing and inserting the fetched page.
 * @param {HTMLElement|null} link
 */
function showPending(link) {
	const menu = link?.closest('collection-menu');
	if (menu && typeof (/** @type {any} */ (menu)).setActive === 'function') {
		/** @type {any} */ (menu).setActive(link);
	}
	for (const el of document.querySelectorAll('collection-infinite-scroll, .collection-grid, .grid-editorial')) {
		el.setAttribute('data-filtering', '');
	}
	document.body.setAttribute('data-collection-navigating', '');
	if (!spinner) {
		spinner = document.createElement('div');
		spinner.className = 'collection-nav__spinner';
		spinner.setAttribute('role', 'status');
		spinner.setAttribute('aria-label', 'Loading');
	}
	if (!spinner.isConnected) document.body.appendChild(spinner);
}

function clearPending() {
	for (const el of document.querySelectorAll('[data-filtering]')) {
		el.removeAttribute('data-filtering');
	}
	document.body.removeAttribute('data-collection-navigating');
	spinner?.remove();
}

/** Resolve on the next rendering opportunity. */
function nextFrame() {
	return new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)));
}

/**
 * Module scripts the fetched content references that this document has not
 * loaded yet (e.g. a custom PLP's editorial video or embed component when
 * coming from a standard PLP). Scripts inserted via DOM APIs don't execute, so
 * each missing one is re-created in <head>; modules load once per URL.
 * @param {Element} root
 */
function loadMissingModules(root) {
	const loaded = new Set([...document.scripts].map((s) => s.src).filter(Boolean));
	for (const script of root.querySelectorAll('script[type="module"][src]')) {
		const src = /** @type {HTMLScriptElement} */ (script).src;
		if (!src || loaded.has(src)) continue;
		loaded.add(src);
		const fresh = document.createElement('script');
		fresh.type = 'module';
		fresh.src = src;
		document.head.appendChild(fresh);
	}
}

/**
 * Swap the page body for the fetched document.
 * @param {Document} doc
 * @param {string} url
 */
function applyDocument(doc, url) {
	const main = document.getElementById('MainContent');
	const nextMain = doc.getElementById('MainContent');
	if (!main || !nextMain) throw new Error('Soft navigation: #MainContent missing');

	document.title = doc.title;

	const canonical = document.querySelector('link[rel="canonical"]');
	const nextCanonical = doc.querySelector('link[rel="canonical"]');
	if (canonical && nextCanonical) canonical.setAttribute('href', nextCanonical.getAttribute('href') || '');

	const nextBody = doc.body;
	if (nextBody) {
		for (const key of ['template', 'templateSuffix']) {
			if (nextBody.dataset[key] != null) document.body.dataset[key] = nextBody.dataset[key];
		}
	}

	// Adopt the new content so custom elements upgrade in this document.
	const fragment = document.createDocumentFragment();
	while (nextMain.firstChild) fragment.appendChild(document.adoptNode(nextMain.firstChild));
	main.replaceChildren(fragment);
	loadMissingModules(main);

	renderedPath = url;
	filterStore.setRenderedUrl?.(url);
	filterStore.syncFromUrl();

	window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

	// Let image preloading, analytics and anything else keyed on collection
	// content know the page changed under them.
	eventBus.emit(new CollectionUpdatedEvent({ data: { type: 'navigated', url } }));
	window.dataLayer?.push({ event: 'soft_navigation', page_path: url, page_title: document.title });
}

/**
 * @param {string} url - path + search
 * @param {{ push?: boolean, link?: HTMLElement|null }} [options]
 */
async function navigate(url, { push = true, link = null } = {}) {
	pending?.abort();
	pending = new AbortController();
	const { signal } = pending;

	showPending(link);

	try {
		const entry = warm.get(url);
		const html = entry && Date.now() - entry.at < WARM_TTL_MS ? await entry.promise : await fetchHtml(url, signal);
		warm.delete(url);
		if (signal.aborted) return;
		if (!html) throw new Error('Soft navigation: empty response');

		const doc = new DOMParser().parseFromString(html, 'text/html');
		if (!doc.getElementById('MainContent')) throw new Error('Soft navigation: no #MainContent');

		// Parsing and inserting are two separate long tasks on a phone; give
		// the spinner a frame between them.
		await nextFrame();
		if (signal.aborted) return;

		if (push) history.pushState({ [STATE_KEY]: true }, '', url);
		applyDocument(doc, url);

		// Keep the spinner until the new content has actually painted.
		await nextFrame();
		await nextFrame();
		if (!signal.aborted) clearPending();
	} catch (error) {
		if (error?.name === 'AbortError') return;
		clearPending();
		console.warn('[collection-nav] falling back to full navigation:', error?.message || error);
		location.href = url;
	} finally {
		if (pending?.signal === signal) pending = null;
	}
}

// ─── Wiring ─────────────────────────────────────────────────────────────────

// Mark the entry we start on so a back from the first soft navigation also
// restores through the swap instead of a reload.
if (/(^|\/)collections\//.test(location.pathname) && !history.state?.[STATE_KEY]) {
	history.replaceState({ ...(history.state || {}), [STATE_KEY]: true }, '', location.href);
}

/**
 * @param {HTMLAnchorElement} link
 * @returns {boolean} true when a navigation was started
 */
function go(link) {
	const url = link.pathname + link.search;
	if (url === renderedPath) return false;
	navigate(url, { push: true, link });
	return true;
}

/** Set by the touch tap handler so the click it synthesizes afterwards is ignored. */
let tapHandledUntil = 0;

document.addEventListener('click', (event) => {
	if (event.defaultPrevented || event.button !== 0) return;
	if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
	const link = /** @type {HTMLAnchorElement|null} */ (/** @type {Element} */ (event.target).closest?.(LINK_SELECTOR));
	if (!isSoftTarget(link)) return;
	event.preventDefault();
	if (performance.now() < tapHandledUntil) return;
	go(link);
});

if (!TOUCH_PRIMARY) {
	// Pointer devices: warm the target page on hover, after a short dwell so a
	// cursor crossing the tabs doesn't fire a fetch per tab. The existing
	// speculation-rules prefetch targets full navigations; the soft navigation
	// fetch() does not reliably reuse it.
	const HOVER_DWELL_MS = 80;
	let hoverTimer = 0;
	document.addEventListener('pointerenter', (event) => {
		if (event.pointerType !== 'mouse') return;
		const link = /** @type {HTMLAnchorElement|null} */ (/** @type {Element} */ (event.target).closest?.(LINK_SELECTOR));
		if (!isSoftTarget(link)) return;
		const url = link.pathname + link.search;
		if (url === renderedPath) return;
		clearTimeout(hoverTimer);
		hoverTimer = window.setTimeout(() => warmUp(url), HOVER_DWELL_MS);
	}, true);
	document.addEventListener('pointerleave', (event) => {
		if (/** @type {Element} */ (event.target).matches?.(LINK_SELECTOR)) clearTimeout(hoverTimer);
	}, true);
	document.addEventListener('pointerdown', (event) => {
		if (event.pointerType !== 'mouse' || event.button !== 0) return;
		const link = /** @type {HTMLAnchorElement|null} */ (/** @type {Element} */ (event.target).closest?.(LINK_SELECTOR));
		if (!isSoftTarget(link)) return;
		const url = link.pathname + link.search;
		if (url !== renderedPath) warmUp(url);
	}, true);
}

if (TOUCH_PRIMARY) {
	// Touch: navigate on touchend ourselves instead of waiting for the click.
	// The category list is a horizontal scroller; iOS swallows the click of a
	// tap that lands while the list is still decelerating (or right as the
	// swapped-in page re-centres it), so taps "didn't take". touchend always
	// fires: a short, still touch is a tap. The warm-up fetch starts on
	// touchstart, 100–300 ms before the finger lifts.
	const TAP_MAX_MS = 400;
	const TAP_MAX_MOVE = 12;
	/** @type {{ link: HTMLAnchorElement, x: number, y: number, at: number } | null} */
	let touch = null;

	document.addEventListener('touchstart', (event) => {
		const link = /** @type {HTMLAnchorElement|null} */ (/** @type {Element} */ (event.target).closest?.(LINK_SELECTOR));
		if (!isSoftTarget(link) || event.touches.length !== 1) { touch = null; return; }
		const t = event.touches[0];
		touch = { link, x: t.clientX, y: t.clientY, at: performance.now() };
		const url = link.pathname + link.search;
		if (url !== renderedPath) warmUp(url);
	}, { passive: true });

	document.addEventListener('touchend', (event) => {
		const current = touch;
		touch = null;
		if (!current) return;
		const t = event.changedTouches[0];
		if (!t) return;
		const moved = Math.hypot(t.clientX - current.x, t.clientY - current.y);
		if (moved > TAP_MAX_MOVE || performance.now() - current.at > TAP_MAX_MS) return;
		if (!/** @type {Element} */ (event.target).closest?.(LINK_SELECTOR)) return;
		tapHandledUntil = performance.now() + 700;
		if (event.cancelable) event.preventDefault();
		go(current.link);
	}, { passive: false });

	document.addEventListener('touchcancel', () => { touch = null; }, { passive: true });
}

window.addEventListener('popstate', (event) => {
	const url = location.pathname + location.search;
	const samePath = url.split('?')[0] === renderedPath.split('?')[0];
	// Same pathname = a filter/sort history entry: the filter store owns it.
	if (samePath) return;
	if (!event.state?.[STATE_KEY] && !/(^|\/)collections\//.test(location.pathname)) {
		location.reload();
		return;
	}
	navigate(url, { push: false });
});
